// MarketPriceCard — AUTODEX 시세 카드 공용 컴포넌트 (W4)
//
// 사용처 (W5/W6):
//   <MarketPriceCard maker="벤츠" model="E클래스" year={2022} mileage={58000}
//                    locale="ko" showUsd={false} />
//   - locale: 'ko' | 'en' | 'ar' | 'ru'  (i18n provider 와 별개 prop)
//   - showUsd: true 면 KRW + USD 동시 (외부 바이어용)
//
// 정책:
//   - debounce 300ms (입력 onBlur/onChange 폭주 방지)
//   - N<3 미노출 (matched:false 면 카드 자체 미렌더)
//   - 출처 표기 0 — "AUTODEX 시세" 단일 브랜딩

import React, { useState, useEffect, useRef } from 'react';
import { API, authFetch } from '../utils/api';

var LABELS = {
  ko: { title: 'AUTODEX 시세 (부가세 제외)', median: '중간값', footer: function(d, n) { return '최근 ' + d + '일 ' + n + '건'; } },
  en: { title: 'AUTODEX Price Range (VAT excluded)', median: 'Median', footer: function(d, n) { return 'Last ' + d + ' days, ' + n + ' trades'; } },
  ar: { title: 'نطاق سعر AUTODEX (باستثناء ضريبة القيمة المضافة)', median: 'الوسيط', footer: function(d, n) { return 'آخر ' + d + ' يوم، ' + n + ' صفقة'; } },
  ru: { title: 'Диапазон цен AUTODEX (без НДС)', median: 'Медиана', footer: function(d, n) { return 'За ' + d + ' дней, ' + n + ' сделок'; } }
};

function fmtNum(n) {
  if (n == null) return '-';
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function fmtKrw(won, locale) {
  if (won == null) return '-';
  if (locale === 'ko') {
    var manwon = Math.round(won / 10000);
    return '₩' + fmtNum(manwon) + '만원';
  }
  return '₩' + fmtNum(won);
}

function fmtUsd(usd) {
  if (usd == null) return null;
  return '$' + fmtNum(usd);
}

function MarketPriceCard(props) {
  var maker = props.maker;
  var model = props.model;
  var year = props.year;
  var mileage = props.mileage;
  var locale = props.locale || 'ko';
  var showUsd = !!props.showUsd;

  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);
  var timerRef = useRef(null);

  useEffect(function() {
    if (!maker || !model || !year || !mileage) { setData(null); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(function() {
      setLoading(true);
      var qs = '?maker=' + encodeURIComponent(maker)
        + '&model=' + encodeURIComponent(model)
        + '&year=' + encodeURIComponent(year)
        + '&mileage=' + encodeURIComponent(mileage);
      authFetch(API + '/external-auction-prices/match' + qs)
        .then(function(r) { return r.json(); })
        .then(function(d) { setData(d); })
        .catch(function() { setData(null); })
        .finally(function() { setLoading(false); });
    }, 300);
    return function() { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [maker, model, year, mileage]);

  // 매칭 실패 / 표본 부족 → 미노출
  if (!data || !data.matched) return null;

  var L = LABELS[locale] || LABELS.en;
  var rangeKrw = fmtKrw(data.p25_krw, locale) + ' ~ ' + fmtKrw(data.p75_krw, locale);
  var medianKrw = fmtKrw(data.median_krw, locale);
  var rangeUsd = (showUsd && data.p25_usd != null)
    ? ' (≈ ' + fmtUsd(data.p25_usd) + ' ~ ' + fmtUsd(data.p75_usd) + ')'
    : '';

  return (
    <div data-testid="market-price-card" style={{
      border: '1px solid #d0d7de',
      borderRadius: 8,
      padding: '12px 16px',
      backgroundColor: '#f7f9fc',
      margin: '12px 0',
      opacity: loading ? 0.6 : 1
    }}>
      <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{L.title}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#1B3A5C' }}>
        {rangeKrw}{rangeUsd}
      </div>
      <div style={{ fontSize: 12, color: '#444', marginTop: 4 }}>
        {L.median} {medianKrw} · {L.footer(data.period_days, data.count)}
      </div>
    </div>
  );
}

export default MarketPriceCard;
